import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { useToast } from "@/hooks/use-toast"
import { ProfileHeader } from "./ProfileHeader"
import { ProfileFormFields, formSchema, type FormData } from "./ProfileFormFields"
import { Card, CardContent } from "@/components/ui/card"
import { ProfileAvatar } from "./ProfileAvatar"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface ProfileFormProps {
  initialData: {
    first_name: string
    last_name: string
    state?: string
    city?: string
    avatar_url?: string
  }
  onProfileUpdate: () => void
}

export function ProfileForm({ initialData, onProfileUpdate }: ProfileFormProps) {
  const { toast } = useToast()
  const [states, setStates] = useState<Array<{ id: number; sigla: string; nome: string }>>([])
  const [cities, setCities] = useState<Array<{ id: number; nome: string }>>([])
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: initialData.first_name,
      last_name: initialData.last_name,
      state: initialData.state || "",
      city: initialData.city || "",
      password: "",
    },
  })

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
        )
        const data = await response.json()
        setStates(data.sort((a: any, b: any) => a.nome.localeCompare(b.nome)))
      } catch (error) {
        console.error("Error fetching states:", error)
        toast({
          variant: "destructive",
          title: "Erro ao carregar estados",
          description: "Não foi possível carregar a lista de estados.",
        })
      } finally {
        setLoading(false)
      }
    }

    fetchStates()
  }, [toast])

  useEffect(() => {
    const fetchCities = async (stateId: string) => {
      if (!stateId) return

      try {
        const state = states.find((s) => s.sigla === stateId)
        if (!state) return

        const response = await fetch(
          `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state.id}/municipios`
        )
        const data = await response.json()
        setCities(data.sort((a: any, b: any) => a.nome.localeCompare(b.nome)))
      } catch (error) {
        console.error("Error fetching cities:", error)
        toast({
          variant: "destructive",
          title: "Erro ao carregar cidades",
          description: "Não foi possível carregar a lista de cidades.",
        })
      }
    }

    const stateValue = form.watch("state")
    if (stateValue) {
      fetchCities(stateValue)
    } else {
      setCities([])
    }
  }, [form.watch("state"), states, toast])

  const onSubmit = async (data: FormData) => {
    setShowSaveDialog(true)
  }

  const handleConfirmSave = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error("User not found")

      // Update password if provided
      if (form.getValues("password")) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: form.getValues("password"),
        })

        if (passwordError) {
          toast({
            variant: "destructive",
            title: "Erro ao atualizar senha",
            description: "Ocorreu um erro ao tentar atualizar sua senha. Por favor, tente novamente.",
          })
          return
        }
      }

      // Update profile information
      const { error } = await supabase
        .from("profiles")
        .update({
          first_name: form.getValues("first_name"),
          last_name: form.getValues("last_name"),
          state: form.getValues("state"),
          city: form.getValues("city"),
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)

      if (error) throw error

      toast({
        title: "Informações alteradas com sucesso!",
        description: form.getValues("password") 
          ? "Suas informações e senha foram atualizadas."
          : "Suas informações foram atualizadas.",
      })
      
      setShowSaveDialog(false)
      setIsEditing(false)
      form.setValue("password", "")
      onProfileUpdate()
    } catch (error) {
      console.error("Error updating profile:", error)
      toast({
        variant: "destructive",
        title: "Erro ao atualizar perfil",
        description: "Ocorreu um erro ao salvar suas informações. Por favor tente novamente.",
      })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          Carregando...
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="flex flex-col items-center space-y-4">
            <ProfileAvatar
              avatarUrl={initialData.avatar_url}
              firstName={initialData.first_name}
              lastName={initialData.last_name}
              onAvatarUpdate={onProfileUpdate}
              isEditing={isEditing}
            />
            <ProfileHeader 
              isEditing={isEditing} 
              onEditClick={() => setIsEditing(!isEditing)} 
            />
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <ProfileFormFields
                form={form}
                isEditing={isEditing}
                states={states}
                cities={cities}
              />
              {isEditing && (
                <Button type="submit" className="w-full">
                  Salvar Alterações
                </Button>
              )}
            </form>
          </Form>
        </div>

        <AlertDialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
              <AlertDialogDescription>
                {form.getValues("password")
                  ? "Tem certeza que deseja salvar as alterações no seu perfil e alterar sua senha?"
                  : "Tem certeza que deseja salvar as alterações no seu perfil?"}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmSave}>
                Confirmar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}